/**
 * Gnosis Pay partner API client (api.gnosispay.com).
 *
 * Endpoints and schemas verified against the live OpenAPI spec on 2026-08-11.
 * Auth: email+OTP signup issues a JWT; we then register Florin's local wallet
 * via POST /eoa-accounts so later sessions re-authenticate with SIWE
 * (24h tokens) instead of emailing the user another code.
 *
 * The full consumer onboarding drives through this client:
 *   signup → terms → Sumsub web KYC → source-of-funds → phone OTP
 *   → gasless Safe deploy (GB → GBPe) → free virtual card.
 */
import { createSiweMessage } from "viem/siwe";
import { GNOSIS_PAY_API } from "../config";
import { getGpToken, setGpToken } from "../db";
import { loadOrCreateWallet, type LocalWallet } from "../wallet";
import type { Hex } from "../types";

export type KycStatus =
  | "notStarted"
  | "documentsRequested"
  | "pending"
  | "processing"
  | "approved"
  | "resubmissionRequested"
  | "rejected"
  | "requiresAction";

export interface GpCard {
  id: string;
  cardToken?: string;
  lastFourDigits: string;
  activatedAt: string | null;
  virtual?: boolean;
  statusCode?: number;
  statusName?: string;
}

export interface GpUser {
  id: string;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  country: string | null;
  kycStatus: KycStatus;
  isSourceOfFundsAnswered: boolean;
  isPhoneValidated: boolean;
  signInWallets: { address: string }[];
  safeWallets: { address: string; chainId?: string; tokenSymbol?: string }[];
  cards: GpCard[];
}

export interface GpTermsEntry {
  type: "general-tos" | "card-monavate-tos" | "cashback-tos" | "privacy-policy";
  currentVersion: string;
  accepted: boolean;
  acceptedVersion?: string | null;
}

export interface GpSafeConfig {
  isDeployed: boolean;
  address: string | null;
  tokenSymbol: string | null;
  fiatSymbol: string | null;
}

export class GpApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
  get isAuth(): boolean {
    return this.status === 401;
  }
}

/** Prefer the proxy until this time once the direct origin fails CORS. */
let proxyUntil = 0;

async function gpFetch(path: string, init: RequestInit): Promise<Response> {
  if (Date.now() >= proxyUntil) {
    try {
      return await fetch(`${GNOSIS_PAY_API.base}${path}`, init);
    } catch (err) {
      // TypeError = network layer (CORS / WAF eating the response). Retry
      // via the Pages-Function proxy: same-origin in production, absolute
      // URL from dev (the function answers CORS preflights for localhost
      // and LAN origins). Re-probe the direct origin after a minute so the
      // app heals itself the moment Gnosis Pay's CORS grant activates.
      if (!(err instanceof TypeError)) throw err;
      proxyUntil = Date.now() + 60_000;
    }
  }
  const base = import.meta.env.DEV ? "https://florin.buzz/gp" : GNOSIS_PAY_API.proxyBase;
  return fetch(`${base}${path}`, init);
}

async function req<T>(
  path: string,
  opts: { method?: string; body?: unknown; auth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  // Only set content-type when there IS a body: a GET with a JSON
  // content-type needlessly forces a CORS preflight and is an unusual
  // request shape to show a WAF.
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (opts.auth !== false) {
    const token = await getGpToken();
    if (!token) throw new GpApiError("Not signed in to Gnosis Pay", 401);
    headers.authorization = `Bearer ${token}`;
  }
  const res = await gpFetch(path, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: AbortSignal.timeout(20_000),
  });
  if (res.status === 401 && opts.auth !== false) {
    await setGpToken(undefined); // session expired — caller re-authenticates
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text.slice(0, 300);
    let code = "";
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (typeof parsed.error === "string") message = parsed.error;
      else if (typeof parsed.message === "string") message = parsed.message;
      if (typeof parsed.code === "string") code = parsed.code;
    } catch {
      // plain-text error body
    }
    if (code === "WAFForbidden") {
      message =
        "Gnosis Pay's firewall blocked this request (WAFForbidden). " +
        "This is network reputation, not an account problem — try again in a few minutes or from a different network.";
    }
    throw new GpApiError(message || `HTTP ${res.status}`, res.status);
  }
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

// ---------- auth ----------

export async function hasSession(): Promise<boolean> {
  return !!(await getGpToken());
}

/**
 * The wallet IS the account (24h JWTs, wiped on any 401): silently
 * (re-)establish a session whenever the stored token is missing. The user
 * should never be told to "sign in again" on a device that holds the key.
 */
export async function ensureSession(): Promise<void> {
  if (await hasSession()) return;
  await siweLogin(await loadOrCreateWallet());
}

/**
 * Run an authenticated call with self-healing auth: establishes a session
 * first, and if the stored token turns out stale (401 mid-call), re-SIWEs
 * once and retries.
 */
export async function withSession<T>(fn: () => Promise<T>): Promise<T> {
  await ensureSession();
  try {
    return await fn();
  } catch (err) {
    if (err instanceof GpApiError && err.isAuth) {
      await siweLogin(await loadOrCreateWallet());
      return fn();
    }
    throw err;
  }
}

export async function signOut(): Promise<void> {
  await setGpToken(undefined);
}

/** Step 1: email the user a 6-digit code. */
export async function requestEmailOtp(email: string): Promise<void> {
  await req("/api/v1/auth/signup/otp", { method: "POST", body: { email }, auth: false });
}

/**
 * Step 2: attach the email to the wallet-authenticated session.
 * Verified live 2026-08-11: /auth/signup requires a Bearer token from the
 * SIWE challenge (wallet-first auth) — without it the API answers
 * 401 "No authorization token was found" before even checking the OTP.
 * `otp` is OPTIONAL per the OpenAPI spec — Gnosis Pay's own reference UI
 * signs up with just { authEmail, partnerId } — so a missing/undelivered
 * code must not dead-end the flow.
 */
export async function signup(email: string, otp?: string): Promise<void> {
  const res = await req<{ id: string; token?: string }>("/api/v1/auth/signup", {
    method: "POST",
    body: {
      authEmail: email,
      ...(otp ? { otp } : {}),
      ...(GNOSIS_PAY_API.partnerId ? { partnerId: GNOSIS_PAY_API.partnerId } : {}),
    },
  });
  if (res.token) await setGpToken(res.token);
}

/**
 * Register Florin's local wallet as a sign-in address, then use SIWE for all
 * future sessions. Safe to call repeatedly; 400 on duplicates is ignored.
 */
export async function ensureSignInWallet(wallet: LocalWallet): Promise<void> {
  try {
    await req("/api/v1/eoa-accounts", { method: "POST", body: { address: wallet.address } });
  } catch (err) {
    // 400/409 = address already registered — the expected steady state.
    // Anything else (500s, WAF blocks) should surface to the caller.
    if (err instanceof GpApiError && (err.status === 400 || err.status === 409)) return;
    throw err;
  }
}

/**
 * SIWE-authenticate the local wallet — the FIRST step of any Gnosis Pay
 * session (their auth is wallet-first; email attaches afterwards). Works for
 * brand-new wallets too. Throws with the API's reason on failure — notably
 * "SIWE domain not allowed" while the partner domain whitelist is inactive.
 */
export async function siweLogin(wallet: LocalWallet): Promise<void> {
  const nonce = String(await req<string>("/api/v1/auth/nonce", { auth: false })).replace(/"/g, "");
  const message = createSiweMessage({
    address: wallet.address as Hex,
    chainId: 100,
    // Gnosis Pay's own partner reference UI (github.com/gnosispay/ui,
    // src/context/AuthContext.tsx) hardcodes THEIR domain in the SIWE
    // message — it never uses the page's host. Partners forking that repo
    // all authenticate with this exact message shape, so it works without
    // the partner-dashboard domain whitelist (whose sync is broken for our
    // account — support ticket open 2026-08-11). Verified live: a fresh
    // wallet + this domain returns a JWT instantly.
    domain: "app.gnosispay.com",
    statement: "Sign in with Ethereum to the app.",
    nonce,
    uri: "https://app.gnosispay.com",
    version: "1",
  });
  const signature = await wallet.account.signMessage({ message });
  const res = await req<{ token: string }>("/api/v1/auth/challenge", {
    method: "POST",
    body: { message, signature, ttlInSeconds: 86_400 },
    auth: false,
  });
  await setGpToken(res.token);
}

// ---------- onboarding state ----------

export async function getUser(): Promise<GpUser> {
  return req<GpUser>("/api/v1/user");
}

export async function getTerms(): Promise<GpTermsEntry[]> {
  // Undocumented in the OpenAPI spec; shape { terms: [...] } confirmed from
  // gnosispay/ui's generated types. Tolerate a bare array anyway.
  const res = await req<{ terms?: GpTermsEntry[] } | GpTermsEntry[]>("/api/v1/user/terms");
  return Array.isArray(res) ? res : (res.terms ?? []);
}

/** A terms entry still needing user action (mirrors gnosispay/ui's filter). */
export function termsNeedAccepting(t: GpTermsEntry): boolean {
  return !t.accepted || (t.acceptedVersion != null && t.acceptedVersion !== t.currentVersion);
}

export async function acceptTerms(type: GpTermsEntry["type"], version: string): Promise<void> {
  await req("/api/v1/user/terms", { method: "POST", body: { terms: type, version } });
}

/** Hosted Sumsub web KYC — open this URL in an iframe/new tab. */
export async function getKycUrl(): Promise<string> {
  const res = await req<{ type: string; url: string }>("/api/v1/kyc/integration");
  return res.url;
}

export interface SofQuestion {
  question: string;
  answers: string[];
}

export async function getSourceOfFundsQuestions(): Promise<SofQuestion[]> {
  return req<SofQuestion[]>("/api/v1/source-of-funds");
}

export async function submitSourceOfFunds(
  answers: { question: string; answer: string }[],
): Promise<void> {
  await req("/api/v1/source-of-funds", { method: "POST", body: answers });
}

export async function requestPhoneOtp(phoneNumber: string): Promise<void> {
  await req("/api/v1/verification", { method: "POST", body: { phoneNumber } });
}

export async function verifyPhone(code: string): Promise<void> {
  await req("/api/v1/verification/check", { method: "POST", body: { code } });
}

// ---------- safe + card ----------

export async function deploySafe(): Promise<void> {
  await req("/api/v1/safe/deploy", { method: "POST", body: {} });
}

export async function getSafeDeployStatus(): Promise<"ok" | "not_deployed" | "processing" | "failed"> {
  const res = await req<{ status: "ok" | "not_deployed" | "processing" | "failed" }>(
    "/api/v1/safe/deploy",
  );
  return res.status;
}

export async function getSafeConfig(): Promise<GpSafeConfig> {
  return req<GpSafeConfig>("/api/v1/safe/config");
}

export async function createVirtualCard(): Promise<string> {
  const res = await req<{ cardId: string }>("/api/v1/cards/virtual", { method: "POST", body: {} });
  return res.cardId;
}

export async function getCards(): Promise<GpCard[]> {
  return req<GpCard[]>("/api/v1/cards");
}

// ---------- IBAN on-ramp (Monerium) ----------
// Bank transfer → personal IBAN → e-money minted straight into the card Safe.

export interface IbanDetails {
  iban: string | null;
  bic: string | null;
  ibanStatus: "NOTSTARTED" | "PENDING" | "PENDING_OAUTH" | "ASSIGNED";
}

export async function ibanAvailable(): Promise<boolean> {
  const res = await req<{ data: { available: boolean } }>("/api/v1/ibans/available");
  return res.data.available;
}

export async function ibanDetails(): Promise<IbanDetails> {
  const res = await req<{ data: IbanDetails }>("/api/v1/ibans/details");
  return res.data;
}

export async function ibanSigningMessage(): Promise<string> {
  const res = await req<{ data: { message: string } }>("/api/v1/ibans/signing-message");
  return res.data.message;
}

/** Reuses the Gnosis Pay KYC to open the Monerium account for our wallet. */
export async function createMoneriumIntegration(signature: string): Promise<void> {
  await req("/api/v1/integrations/monerium", { method: "POST", body: { signature } });
}

// ---------- Safe owners + relayed withdrawals (off-ramp primitive) ----------

export async function getSafeOwners(): Promise<string[]> {
  const res = await req<{ data: { owners: string[] } }>("/api/v1/owners");
  return res.data.owners;
}

export interface WithdrawTypedData {
  domain: { verifyingContract: Hex; chainId: number };
  primaryType: string;
  types: Record<string, { name: string; type: string }[]>;
  message: { salt: string; data: string } & Record<string, unknown>;
}

/**
 * EIP-712 payload for withdrawing from the card Safe. Signed by a Safe owner
 * (Florin's wallet, registered at onboarding), then relayed by Gnosis Pay
 * through the Delay module — executes ~3 minutes later, no gas needed.
 */
export async function getWithdrawTypedData(
  amountUnits: bigint,
  to: Hex,
  tokenAddress: Hex,
): Promise<WithdrawTypedData> {
  const q = new URLSearchParams({
    amount: amountUnits.toString(),
    to,
    tokenAddress,
  });
  const res = await req<{ data: WithdrawTypedData }>(
    `/api/v1/accounts/withdraw/transaction-data?${q}`,
  );
  return res.data;
}

export async function submitWithdraw(params: {
  amountUnits: bigint;
  to: Hex;
  tokenAddress: Hex;
  signature: string;
  message: WithdrawTypedData["message"];
}): Promise<void> {
  await req("/api/v1/accounts/withdraw", {
    method: "POST",
    body: {
      amount: params.amountUnits.toString(),
      to: params.to,
      tokenAddress: params.tokenAddress,
      signature: params.signature,
      message: params.message,
    },
  });
}
